// #403 — the G100 setting controls, moved verbatim from the Test Console's Settings card (#379)
// so the customer sheet (CameraG100SettingsSheet) and the console draw the same controls for the
// same 33 settings. Two additions: `choiceLabel` (the caller's words for a choice; the console
// keeps the document's value names) and `disabled` (a user who cannot control the camera).
import { useTranslation } from 'react-i18next'
import * as Switch from '@radix-ui/react-switch'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { G100_MAX_WINDOWS, G100_WINDOW, G100_ZONE_ALL, G100_ZONE_CELLS, type G100SettingSpec } from '@/lib/g100Settings'

export interface Species { speciesName: string; enable: number; threshold: number }

export const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
export const show = (value: unknown) => (typeof value === 'object' && value !== null ? JSON.stringify(value) : value)
export const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '')
export const isWhole = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v)

/** The server's reason for a refusal, in one line (as the page's Commands card). */
export function reason(error: unknown): string {
  const data = (error as { response?: { data?: { errors?: Record<string, unknown>; detail?: unknown } } })
    ?.response?.data
  if (data?.errors) return Object.entries(data.errors).map(([k, v]) => `${k}: ${String(v)}`).join('; ')
  if (data?.detail) return String(data.detail)
  return ''
}
export const statusOf = (error: unknown) => (error as { response?: { status?: number } })?.response?.status
export const fieldErrors = (error: unknown): Record<string, string> => {
  const errors = (error as { response?: { data?: { errors?: Record<string, unknown> } } })?.response?.data?.errors
  return errors ? Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, String(v)])) : {}
}

/** A draft value that cannot be sent: a window that is not HHMM-HHMM-days, a number box left empty. */
export function invalid(spec: G100SettingSpec, value: unknown): boolean {
  if (spec.kind === 'windows') return !Array.isArray(value) || value.some((w) => typeof w !== 'string' || !G100_WINDOW.test(w))
  if (spec.kind === 'range') return !isWhole(value)
  return false
}

const SWITCH = cn(
  'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  'data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-muted-foreground/30',
  'disabled:cursor-not-allowed disabled:opacity-50',
)
const THUMB = 'pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0'
export const INPUT = 'rounded border border-border bg-background px-1.5 py-1 font-mono text-xs disabled:cursor-not-allowed disabled:opacity-50'

export function OnOff({ label, checked, onChange, disabled }: {
  label: string; checked: boolean; onChange: (on: boolean) => void; disabled?: boolean
}) {
  return (
    <Switch.Root aria-label={label} checked={checked} onCheckedChange={onChange} disabled={disabled} className={SWITCH}>
      <Switch.Thumb className={THUMB} />
    </Switch.Root>
  )
}

export function Control({ spec, value, onChange, choiceLabel, disabled }: {
  spec: G100SettingSpec
  value: unknown
  onChange: (v: unknown) => void
  choiceLabel?: (choice: number) => string   // #403: the caller's words; default = the document's value names
  disabled?: boolean
}) {
  const { t } = useTranslation('g100Console')
  switch (spec.kind) {
    case 'choice': {
      // The document's value names, one object per field in the locale; the number itself when a
      // label is missing.
      const labels = t(`settings.choices.${spec.name}`, { returnObjects: true }) as unknown
      const label = choiceLabel ?? ((c: number) =>
        (labels && typeof labels === 'object' ? (labels as Record<string, string>)[String(c)] : undefined) ?? String(c))
      return (
        <select aria-label={spec.name} value={value === undefined ? '' : String(value)} className={INPUT} disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}>
          {value === undefined && <option value="" />}
          {spec.choices.map((c) => (
            <option key={c} value={String(c)}>{label(c)}</option>
          ))}
        </select>
      )
    }
    case 'switch':
      return <OnOff label={spec.name} checked={value === 1} onChange={(on) => onChange(on ? 1 : 0)} disabled={disabled} />
    case 'range':
      return (
        <input type="number" aria-label={spec.name} min={spec.min} max={spec.max} step={1} className={cn(INPUT, 'w-24')}
          disabled={disabled}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => { const n = Number(e.target.value); onChange(e.target.value !== '' && Number.isFinite(n) ? n : e.target.value) }} />
      )
    case 'timezone':
      return (
        <input type="text" aria-label={spec.name} className={cn(INPUT, 'w-44')} spellCheck={false} disabled={disabled}
          value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
      )
    case 'windows':
      return <Windows name={spec.name} value={Array.isArray(value) ? (value as string[]) : []} onChange={onChange} disabled={disabled} />
    case 'species':
      return <SpeciesTable value={Array.isArray(value) ? (value as Species[]) : []} onChange={onChange} disabled={disabled} />
    case 'zone':
      return <Zone value={isWhole(value) ? value : 0} onChange={onChange} disabled={disabled} />
  }
}

export function Windows({ name, value, onChange, disabled }: {
  name: string; value: string[]; onChange: (v: string[]) => void; disabled?: boolean
}) {
  const { t } = useTranslation('g100Console')
  const bad = value.some((w) => !G100_WINDOW.test(w))
  return (
    <div className="space-y-1">
      {value.length === 0 && <p className="text-xs text-muted-foreground">{t('settings.windowsEmpty')}</p>}
      {value.map((w, i) => (
        <div key={i} className="flex items-center gap-2">
          <input type="text" aria-label={`${name} ${i + 1}`} value={w} spellCheck={false} className={cn(INPUT, 'w-44')} disabled={disabled}
            onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))} />
          <Button size="sm" variant="ghost" aria-label={t('settings.windowRemove', { index: i + 1 })} disabled={disabled}
            onClick={() => onChange(value.filter((_, j) => j !== i))}>×</Button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" aria-label={`${name}: ${t('settings.windowAdd')}`}
          disabled={disabled || value.length >= G100_MAX_WINDOWS} onClick={() => onChange([...value, ''])}>
          {t('settings.windowAdd')}
        </Button>
        <span className="text-xs text-muted-foreground">{t('settings.windowHint')}</span>
      </div>
      {bad && <p className="text-xs text-destructive">{t('settings.windowInvalid')}</p>}
    </div>
  )
}

export function SpeciesTable({ value, onChange, disabled }: {
  value: Species[]; onChange: (v: Species[]) => void; disabled?: boolean
}) {
  const { t } = useTranslation('g100Console')
  const update = (i: number, patch: Partial<Species>) => onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="py-0.5 font-normal">{t('settings.species.name')}</th>
          <th className="py-0.5 font-normal">{t('settings.species.enable')}</th>
          <th className="py-0.5 font-normal">{t('settings.species.threshold')}</th>
        </tr>
      </thead>
      <tbody>
        {value.map((s, i) => (
          <tr key={s.speciesName} className="border-t border-border/30">
            <td className="py-0.5 font-mono">{s.speciesName}</td>
            <td className="py-0.5">
              <OnOff label={`${s.speciesName} ${t('settings.species.enable')}`} checked={s.enable === 1}
                onChange={(on) => update(i, { enable: on ? 1 : 0 })} disabled={disabled} />
            </td>
            <td className="py-0.5">
              <input type="number" min={1} max={100} step={1} className={cn(INPUT, 'w-20')} disabled={disabled}
                aria-label={`${s.speciesName} ${t('settings.species.threshold')}`} value={String(s.threshold)}
                onChange={(e) => update(i, { threshold: Number(e.target.value) })} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function Zone({ value, onChange, disabled }: { value: number; onChange: (v: number) => void; disabled?: boolean }) {
  const { t } = useTranslation('g100Console')
  const cells = Array.from({ length: G100_ZONE_CELLS }, (_, i) => i)
  return (
    <div className="space-y-1">
      <div className="grid w-fit grid-cols-5 gap-1">
        {cells.map((i) => (
          <input key={i} type="checkbox" aria-label={t('settings.zoneCell', { cell: i + 1 })} className="h-5 w-5" disabled={disabled}
            checked={((value >> i) & 1) === 1} onChange={() => onChange(value ^ (1 << i))} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-mono">{t('settings.zoneValue', { value })}</span>
        <Button size="sm" variant="ghost" disabled={disabled} onClick={() => onChange(G100_ZONE_ALL)}>{t('settings.zoneAll')}</Button>
        <Button size="sm" variant="ghost" disabled={disabled} onClick={() => onChange(0)}>{t('settings.zoneNone')}</Button>
        <span className="text-muted-foreground">{t('settings.zoneHint')}</span>
      </div>
    </div>
  )
}
